import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { CollapseArrowComponent } from "../collapse-arrow/collapse-arrow.component";
import { NavigationShareService } from '../../../core/services/navigation-share.service';
import { getUuid } from 'pdfjs-dist';

@Component({
  selector: 'app-drop-down-section',
  standalone: true,
  imports: [CommonModule, CollapseArrowComponent],
  templateUrl: './drop-down-section.component.html',
  styleUrl: './drop-down-section.component.css'
})
export class DropDownSectionComponent implements OnInit, AfterViewInit, OnDestroy {
  constructor(private navigationShareService: NavigationShareService) { }

  @Input() title = '';
  @Input() titleIcon?: string;

  @Input() defaultOpen = false;

  @Input() onEditClicked?: () => void;
  @Input() showEdit = false;
  @Output() edit = new EventEmitter<void>();

  @Output() openedChange = new EventEmitter<boolean>();

  @Input() enableFloatingActiong: boolean = false;
  @Input() sectionId: string = getUuid();

  isOpen = this.defaultOpen;

  @ViewChild('sectionRoot', { static: true }) sectionRoot!: ElementRef<HTMLElement>;
  @ViewChild('mainHeader', { static: true }) mainHeader!: ElementRef<HTMLElement>;

  isSectionInView = false;
  isHeaderInView = true;

  private sectionObserver?: IntersectionObserver;
  private headerObserver?: IntersectionObserver;

  get showFloatingHeader(): boolean {
    if (!this.enableFloatingActiong) { return false; }
    const wantsToFloat = this.isSectionInView && !this.isHeaderInView;

    const activeId = this.navigationShareService.getState('activeHeader');

    if (wantsToFloat && !activeId) {
      this.navigationShareService.setState('activeHeader', this.sectionId);
      return true;
    }

    if (activeId === this.sectionId) {
      return wantsToFloat;
    }

    return false;
  }

  ngAfterViewInit(): void {
    if (!this.enableFloatingActiong) { return; }
    
    const sectionEl = this.sectionRoot.nativeElement;
    const headerEl = this.mainHeader.nativeElement;

    // Watch if the whole section is in the viewport
    this.sectionObserver = new IntersectionObserver(
      ([entry]) => {
        this.isSectionInView = entry.isIntersecting;

        if (!this.isSectionInView) {
          const active = this.navigationShareService.getState('activeHeader');
          if (active === this.sectionId) {
            this.navigationShareService.clearState('activeHeader');
          }
        }
      },
      {
        threshold: 0.01,
      }
    );

    // Watch if the original header is visible
    this.headerObserver = new IntersectionObserver(
      ([entry]) => {
        this.isHeaderInView = entry.isIntersecting;

        if (this.isHeaderInView) {
          const active = this.navigationShareService.getState('activeHeader');
          if (active === this.sectionId) {
            this.navigationShareService.clearState('activeHeader');
          }
        }
      },
      {
        threshold: 0.01,
      }
    );

    this.sectionObserver.observe(sectionEl);
    this.headerObserver.observe(headerEl);
  }

  ngOnDestroy(): void {
    this.sectionObserver?.disconnect();
    this.headerObserver?.disconnect();
  }

  ngOnInit() {
    this.isOpen = this.defaultOpen;
  }

  toggle() {
    this.isOpen = !this.isOpen;
    this.openedChange.emit(this.isOpen);
  }

  handleEditClick(e: MouseEvent) {
    e.stopPropagation(); // don't toggle
    if (this.onEditClicked) this.onEditClicked();
    this.edit.emit();
  }

  onHeaderKeydown(e: KeyboardEvent) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      this.toggle();
    }
  }
}
